import { type ProductFruitsUserObject, productFruits } from 'product-fruits';
import { ref } from 'vue';
import config from '@/config';
import { devLog } from '@/helpers/dev';
import router from '@/router';
import { useCompanyStore } from '@/stores/company';
import { useLocalizationStore } from '@/stores/localization';
import { useLocationsStore } from '@/stores/locations';
import { useUserStore } from '@/stores/user';
import { device } from '@/user-context';

export const unreadNewsCount = ref(0);
export const isNewsWidgetOpen = ref(false);

// The newsfeed panel isn't exposed through the JS API, so we track its open
// state by observing ProductFruits' own shadow DOM (undocumented, but the
// panel is fully added/removed from the DOM on open/close, so this is stable).
let newsWidgetOpenStateObservers: MutationObserver[] = [];

// On mobile the newsfeed panel doesn't fully capture touch scrolling, so a
// swipe that reaches the panel's own scroll boundary bleeds through to the
// page behind it. Locking body scroll while the panel is open prevents that.
let lockedScrollY = 0;

const lockBackgroundScroll = () => {
  lockedScrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
};

const unlockBackgroundScroll = () => {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  window.scrollTo(0, lockedScrollY);
};

const observeNewsWidgetOpenState = () => {
  disconnectNewsWidgetOpenStateObservers();

  const attachObserver = (): boolean => {
    const shadowRoot = document.querySelector(
      '.productfruits--container',
    )?.shadowRoot;
    if (!shadowRoot) {
      return false;
    }

    const update = () => {
      const isOpen = !!shadowRoot.querySelector('.pfruits-widget-newsfeed');
      if (isOpen === isNewsWidgetOpen.value) {
        return;
      }
      isNewsWidgetOpen.value = isOpen;
      if (!device.mobile) {
        return;
      }
      if (isOpen) {
        lockBackgroundScroll();
      } else {
        unlockBackgroundScroll();
      }
    };
    update();
    const shadowObserver = new MutationObserver(update);
    shadowObserver.observe(shadowRoot, { childList: true, subtree: true });
    newsWidgetOpenStateObservers.push(shadowObserver);
    return true;
  };

  if (attachObserver()) {
    return;
  }

  const bodyObserver = new MutationObserver(() => {
    if (attachObserver()) {
      bodyObserver.disconnect();
    }
  });
  bodyObserver.observe(document.body, { childList: true });
  newsWidgetOpenStateObservers.push(bodyObserver);
};

const disconnectNewsWidgetOpenStateObservers = () => {
  newsWidgetOpenStateObservers.forEach((observer) => {
    observer.disconnect();
  });
  newsWidgetOpenStateObservers = [];
};

const productFruitsService = {
  init() {
    const workspaceCode = config.productFruitsWorkspaceCode;
    const data = this.baseData();
    if (!workspaceCode || !data) {
      devLog('Product Fruits failed to initialize', { workspaceCode, data });
      return;
    }

    const { language } = useLocalizationStore();
    devLog('Initializing Product Fruits', {
      language,
      username: data.username,
    });

    productFruits.init(workspaceCode, language, data, {
      customNavigation: {
        use: true,
        onGet: () => window.location.pathname + window.location.search,
        navigate: (location) => {
          const url = /^https?:\/\//.test(location) ? new URL(location) : null;
          const path = url ? url.pathname + url.search + url.hash : location;
          const resolved = router.resolve(path);
          if (resolved.name && resolved.name !== '404') {
            // The recorded target's companyId/locationId route params reflect
            // wherever the CTA was originally captured, not this visitor's own
            // company — pushing them as-is trips the "wrong company" guard in
            // router/index.ts. Swap in the current company/location instead.
            const { companyId } = useCompanyStore();
            const { locationId } = useLocationsStore();
            router.push({
              name: resolved.name,
              params: {
                ...resolved.params,
                ...('companyId' in resolved.params
                  ? { companyId: companyId?.toString() ?? '' }
                  : {}),
                ...('locationId' in resolved.params
                  ? { locationId: locationId?.toString() ?? '' }
                  : {}),
              },
              query: resolved.query,
            });
          } else {
            window.open(location, '_blank');
          }
        },
      },
    });
    observeNewsWidgetOpenState();

    // Registered before attachNewsWidget() so we also get the initial unread count.
    productFruits.safeExec(() => {
      window.productFruits?.api?.announcementsV2?.listen(
        'newsfeed-unread-count-changed',
        (data: { count: number }) => {
          unreadNewsCount.value = data.count;
        },
      );
    });
    devLog('Product Fruits initialized', data);
  },

  attachNewsWidget(element: HTMLElement) {
    productFruits.safeExec(() => {
      window.productFruits?.api?.announcementsV2?.attachNewsWidgetToElement(
        element,
      );
    });
  },

  showFeedbackWidget() {
    productFruits.safeExec(() => {
      window.productFruits?.api?.feedback?.showModal();
    });
  },

  baseData() {
    const { user } = useUserStore();
    if (!user) {
      return null;
    }

    const { language } = useLocalizationStore();
    const { company } = useCompanyStore();

    return {
      username: user.email,
      email: user.email,
      firstname: user.firstName,
      lastname: user.lastName ?? undefined,
      signUpAt: user.createdAt,
      props: {
        language: language,
        company_country: company.country,
        company_subscription_status:
          company.activeSubscription?.stripeStatus ?? '',
        company_trial_ends_at: company.trialEndAt,
        company_subscription_plan: company.activeSubscription?.plan ?? '',
      },
    };
  },

  updateData(data: Partial<ProductFruitsUserObject['props']>) {
    const baseData = this.baseData();
    if (!baseData) {
      devLog('Product Fruits failed to update data', { data });
      return;
    }

    productFruits.safeExec(() => {
      window.productFruits?.identifyUser?.({
        ...baseData,
        props: {
          ...baseData?.props,
          ...data,
        },
      });
      devLog('Product Fruits updated data', { data });
    });
  },

  destroy() {
    try {
      (window.productFruits as any)?.services?.destroy();
    } catch (error) {
      devLog('Error destroying Product Fruits', { error });
    } finally {
      disconnectNewsWidgetOpenStateObservers();
      if (isNewsWidgetOpen.value && device.mobile) {
        unlockBackgroundScroll();
      }
      unreadNewsCount.value = 0;
      isNewsWidgetOpen.value = false;
      devLog('Product Fruits destroyed', {});
    }
  },
};

if (config.isTest) {
  (window as any).productFruitsService = productFruitsService;
}

export default productFruitsService;
